/**
 * Типы раздела push-рассылок (`/v1/marketing/*`, `/v1/push/*`).
 *
 * Повторяют сериализаторы приложения `marketing_push` один в один. Здесь же
 * русские подписи перечислений и расшифровка кодов ошибок FCM — чтобы
 * формулировки не расходились между списком, формой и отчётом.
 */

export type Platform = 'android' | 'ios';

export type CampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'queued'
  | 'running'
  | 'handed_off'
  | 'delivered'
  | 'partially_delivered'
  | 'failed'
  | 'cancelled';

export type EngagementMode = 'ignore' | 'more' | 'less' | 'between';

export type DeliveryStatus = 'queued' | 'sent' | 'delivered' | 'failed' | 'skipped';

/* -------------------------------------------------------------------------- */
/* Форматированный текст                                                       */
/* -------------------------------------------------------------------------- */

export const RICHTEXT_VERSION = 1;

export type RichMark = 'bold' | 'italic' | 'underline' | 'strike' | 'link';

export interface RichSpan {
  text: string;
  marks?: RichMark[];
  href?: string;
}

export type RichBlock =
  | { type: 'paragraph'; spans: RichSpan[] }
  | { type: 'heading'; level: 2 | 3; spans: RichSpan[] }
  | { type: 'list'; ordered: boolean; items: RichSpan[][] };

export interface RichTextDocument {
  v: number;
  blocks: RichBlock[];
}

/* -------------------------------------------------------------------------- */
/* Справочники                                                                 */
/* -------------------------------------------------------------------------- */

export interface PushAppTarget {
  id: number;
  code: string;
  display_name: string;
  platform: Platform;
  is_active: boolean;
  /** Живых установок. Показывается рядом с чекбоксом, чтобы «отправил, а
   *  никто не получил» было видно до отправки, а не после. */
  installations: number;
}

export interface PushApp {
  id: number;
  code: string;
  name: string;
  is_active: boolean;
  targets: PushAppTarget[];
}

export interface PushLanguage {
  code: string;
  name: string;
}

export interface PushNotificationType {
  id: number;
  code: string;
  name: string;
  requires_consent: boolean;
  is_active: boolean;
  sort_order: number;
}

/* -------------------------------------------------------------------------- */
/* Контент                                                                     */
/* -------------------------------------------------------------------------- */

/** Ориентиры для предупреждения об обрезании. Не валидация: реальная граница
 *  зависит от устройства, оболочки и размера шрифта в настройках. */
export interface PushSoftLimits {
  android: { title: number; body: number };
  ios: { title: number; body: number };
}

export interface PushContent {
  language: string;
  /** Системная плашка. */
  push_title: string;
  push_body: string;
  /** Экран внутри приложения. */
  inapp_title: string;
  inapp_body: RichTextDocument;
  inapp_body_plain?: string;
  navigation_title: string;
  button_label: string;
  button_url: string;
  image_url: string;
  soft_limits?: PushSoftLimits;
}

/* -------------------------------------------------------------------------- */
/* Рассылка                                                                    */
/* -------------------------------------------------------------------------- */

export interface PushAudienceParams {
  engagement_mode: EngagementMode;
  engagement_days: number | null;
  engagement_from_days: number | null;
  engagement_to_days: number | null;
}

export interface PushCampaign extends PushAudienceParams {
  id: number;
  public_id: string;
  notification_type: string;
  targets: string[];
  contents: PushContent[];
  is_test: boolean;
  scheduled_at: string | null;
  schedule_timezone: string;
  status: CampaignStatus;
  status_display: string;
  estimated_audience: number | null;
  locked_audience_size: number | null;
  author_name: string;
  created_at: string;
  started_at: string | null;
  handed_off_at: string | null;
  finished_at: string | null;
  error_message: string;
}

/** Черновик в форме: без серверных полей, которые задать нельзя. */
export type PushCampaignDraft = PushAudienceParams & {
  id?: number;
  notification_type: string;
  targets: string[];
  contents: PushContent[];
  is_test: boolean;
  scheduled_at: string | null;
  schedule_timezone: string;
};

export interface PushCampaignListItem {
  id: number;
  public_id: string;
  title: string;
  notification_type: string;
  targets: string[];
  languages: string[];
  status: CampaignStatus;
  status_display: string;
  is_test: boolean;
  scheduled_at: string | null;
  locked_audience_size: number | null;
  created_at: string;
  finished_at: string | null;
}

export interface PushTemplate extends PushAudienceParams {
  id: number;
  name: string;
  notification_type: string | null;
  targets: string[];
  contents: PushContent[];
  created_at: string;
  updated_at: string;
}

/* -------------------------------------------------------------------------- */
/* Аудитория и отчёты                                                          */
/* -------------------------------------------------------------------------- */

export interface PushAudienceEstimate {
  total: number;
  by_target: Record<string, number>;
  by_language: Record<string, number>;
  /** Установки, чей язык не выбран в рассылке. */
  excluded_no_language: number;
  /** Установки без согласия на рекламу. Ненулевое только у типов,
   *  требующих согласия. */
  excluded_no_consent: number;
  inactive_targets: string[];
  target_installations: Record<string, number>;
  is_estimate: boolean;
  note: string;
}

export interface PushValidationResult {
  ok: boolean;
  problems: string[];
}

export interface PushReport {
  status: CampaignStatus;
  locked_audience_size: number | null;
  totals: {
    total: number;
    sent: number;
    delivered: number;
    failed: number;
    skipped: number;
    queued: number;
    read: number;
    clicked: number;
  };
  by_error: Record<string, number>;
  by_target: Record<string, { total: number; accepted: number }>;
}

export interface PushDeliveryErrorRow {
  installation_id: number;
  app_target__code: string;
  language__code: string;
  status: DeliveryStatus;
  error_code: string;
  error_message: string;
  attempts: number;
}

/* -------------------------------------------------------------------------- */
/* Медиа и тестовые устройства                                                 */
/* -------------------------------------------------------------------------- */

export interface PushImage {
  id: number;
  url: string;
  width: number;
  height: number;
  size: number;
  content_type: string;
  original_name: string;
  created_at: string;
}

export interface PushTestDevice {
  id: number;
  label: string;
  app_target: string;
  platform: Platform;
  language: string;
  app_version: string;
  last_seen_at: string;
  is_active: boolean;
  created_at: string;
}

/* -------------------------------------------------------------------------- */
/* Служебное                                                                   */
/* -------------------------------------------------------------------------- */

/** LimitOffsetPagination ядра без PAGE_SIZE: конверт приходит только при
 *  `?limit=`, иначе голый массив. Нормализуется через `asList()`. */
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface PushCampaignFilters {
  status?: CampaignStatus[];
  type?: string;
  target?: string;
  language?: string;
  search?: string;
  include_test?: boolean;
  limit?: number;
  offset?: number;
}

/* -------------------------------------------------------------------------- */
/* Подписи                                                                     */
/* -------------------------------------------------------------------------- */

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

export const CAMPAIGN_STATUS_LABEL: Record<CampaignStatus, { label: string; tone: BadgeTone }> = {
  draft: { label: 'Черновик', tone: 'neutral' },
  scheduled: { label: 'Запланирована', tone: 'info' },
  queued: { label: 'Рассылка создана', tone: 'info' },
  running: { label: 'Отправка выполняется', tone: 'accent' },
  handed_off: { label: 'Передано в сервис push', tone: 'accent' },
  delivered: { label: 'Доставлено', tone: 'success' },
  partially_delivered: { label: 'Доставлено частично', tone: 'warning' },
  failed: { label: 'Ошибка', tone: 'danger' },
  cancelled: { label: 'Отменено', tone: 'neutral' },
};

export const DELIVERY_STATUS_LABEL: Record<DeliveryStatus, string> = {
  queued: 'В очереди',
  sent: 'Принято FCM',
  delivered: 'Подтверждено приложением',
  failed: 'Ошибка',
  skipped: 'Пропущено',
};

/**
 * Расшифровка кодов ошибок FCM.
 *
 * Маркетологу `UNREGISTERED` ничего не говорит, а решение по кодам разное:
 * по одним делать нечего (пользователь удалил приложение), по другим нужен
 * разработчик (сломаны креденшелы или APNs-ключ).
 */
export const PUSH_ERROR_CODES: Record<
  string,
  { label: string; hint: string; tone: BadgeTone }
> = {
  UNREGISTERED: {
    label: 'Приложение удалено',
    hint: 'Пользователь удалил приложение или отключил уведомления. Установка исключена из будущих рассылок автоматически.',
    tone: 'neutral',
  },
  SENDER_ID_MISMATCH: {
    label: 'Токен от другого проекта',
    hint: 'Устройство зарегистрировано в другом Firebase-проекте. Установка исключена автоматически.',
    tone: 'neutral',
  },
  INVALID_ARGUMENT: {
    label: 'Некорректный запрос',
    hint: 'Ошибка на нашей стороне, а не у получателя. Сообщите разработчику.',
    tone: 'danger',
  },
  UNAVAILABLE: {
    label: 'FCM временно недоступен',
    hint: 'Повторная отправка выполняется автоматически.',
    tone: 'warning',
  },
  INTERNAL: {
    label: 'Внутренняя ошибка FCM',
    hint: 'Повторная отправка выполняется автоматически.',
    tone: 'warning',
  },
  QUOTA_EXCEEDED: {
    label: 'Превышена квота FCM',
    hint: 'Повторная отправка выполняется автоматически, с задержкой.',
    tone: 'warning',
  },
  THIRD_PARTY_AUTH_ERROR: {
    label: 'Проблема с APNs',
    hint: 'Ключ APNs в консоли Firebase истёк или неверен. Нужен разработчик.',
    tone: 'danger',
  },
  UNAUTHENTICATED: {
    label: 'Ошибка доступа к FCM',
    hint: 'Проблема с креденшелами сервиса. Нужен разработчик.',
    tone: 'danger',
  },
  NO_CONTENT: {
    label: 'Нет контента для языка',
    hint: 'Язык получателя был убран из рассылки после запуска.',
    tone: 'warning',
  },
  INSTALLATION_INACTIVE: {
    label: 'Установка отключена',
    hint: 'Пользователь отключил уведомления между расчётом аудитории и отправкой.',
    tone: 'neutral',
  },
};

/**
 * Режимы фильтра по активности.
 *
 * Подписи развёрнутые намеренно. На бэкенде операторы называются `more` и
 * `less`, и в старом сервисе они никак не расшифровывались — из-за чего
 * рассылку легко было отправить ровно противоположной группе.
 */
export const ENGAGEMENT_MODE_OPTIONS: {
  value: EngagementMode;
  label: string;
  hint: string;
}[] = [
  { value: 'ignore', label: 'Не учитывать', hint: 'Все установки, независимо от активности.' },
  {
    value: 'less',
    label: 'Был активен за последние N дней',
    hint: 'Только те, кто заходил в приложение недавно.',
  },
  {
    value: 'more',
    label: 'Не заходил дольше N дней',
    hint: 'Только те, кто давно не открывал приложение.',
  },
  { value: 'between', label: 'Заходил в диапазоне дней', hint: 'Например, от 7 до 30 дней назад.' },
];
